/**
 * UC Berkeley brand colors (W56 Worker B, S3 — theme).
 *
 * Source, two first-party captures that agree (fact sheet brand.*, `verified`):
 *   - brand.berkeley.edu Colors page, captured 2026-10-29
 *     (admin_tools/backend/data/onboarding/ucberkeley/probes/brand_colors.html):
 *     "Berkeley Blue Hex 002676", "California Gold Hex FDB515" (primary palette);
 *   - the catalog's own theme (probes/bootstrap.json catalogSettings: theme #002676,
 *     themeLightest #FDB515).
 * The brief's #003262 is an older Berkeley Blue that is NOT on the current brand page.
 *
 * Contrast (WCAG, measured against the token surfaces in index.css — card #FFFFFF, paper #F5F1EB,
 * dark #161412 / #1F1C19 — by admin_tools/backend/data/onboarding/ucberkeley/s3/theme_colors.py,
 * which regenerates every value below):
 *   Berkeley Blue #002676: white text on it 13.65:1; as text 13.65:1 on the card, 12.13:1 on paper.
 *     `-strong` = the brand itself (white text already clears 7:1).
 *   California Gold #FDB515 fails as TEXT on light surfaces (1.78 / 1.58), and `text-accent`
 *     renders at 12 px, so 4.5:1 applies. The accent TOKEN is the gold's own hue darkened until it
 *     clears 4.5:1 on BOTH light surfaces: #916501 = 5.16:1 on the card, 4.59:1 on paper. On the dark
 *     surfaces it measures 3.56:1 (#161412) / 3.28:1 (#1F1C19) — the 3:1 floor only; the theme system
 *     has no dark-mode accent token (doc 51 S3-6; the Mission / De Anza / SDSU / Foothill record).
 *     California Gold itself is the -400 step, for fills.
 * Ramps are HSL ladders pinned at the brand values, lightness strictly decreasing 50 -> 900 (the
 * script asserts it); Berkeley Blue is -700 (the UC Merced / SJSU convention for a dark brand blue).
 * Not an officially published extended palette. Text-only branding, no logo asset embedded.
 *
 * Brand mark (components/ui/BrandMark.tsx paints three faces from `--color-mark-*`; a theme without
 * them inherits UMD's red from index.css). The cross-theme distance floor was retired (owner decision
 * 2026-10-14), so the mark follows Berkeley's own blue, taken from the ladder above:
 *   light: front = Berkeley Blue (the brand IS the front face, UC Merced precedent), top -800, side -900;
 *          front on paper 12.13:1.
 *   dark:  re-derived lighter — front -400 (4.17:1 on #161412), top -500 (2.73:1), side -600 (1.83:1,
 *          reads as shadow; UC Merced ships the same 1.83:1 side facet). Logos are WCAG
 *          contrast-exempt; the front face clears the 3:1 non-text floor and carries identity.
 */
import type { ThemeColors } from '../types';

export const ucberkeleyColors: ThemeColors = {
  // Primary — Berkeley Blue (#002676 = rgb(0, 38, 118)); -strong = the brand (white text 13.65:1).
  '--color-primary':        '0 38 118',
  '--color-primary-strong': '0 38 118',
  '--color-primary-50':  '240 245 255',
  '--color-primary-100': '214 227 255',
  '--color-primary-200': '173 200 255',
  '--color-primary-300': '112 158 255',
  '--color-primary-400': '41 110 255',
  '--color-primary-500': '0 74 230',
  '--color-primary-600': '0 54 168',
  '--color-primary-700': '0 38 118',
  '--color-primary-800': '0 28 87',
  '--color-primary-900': '0 18 56',

  // Accent — California Gold's hue (#FDB515 = rgb(253, 181, 21)); token darkened to #916501 for text.
  '--color-accent':     '145 101 1',
  '--color-accent-50':  '255 249 235',
  '--color-accent-100': '255 239 204',
  '--color-accent-200': '254 223 154',
  '--color-accent-300': '254 204 93',
  '--color-accent-400': '253 181 21',
  '--color-accent-500': '145 101 1',
  '--color-accent-600': '116 81 1',
  '--color-accent-700': '91 63 1',
  '--color-accent-800': '66 46 1',
  '--color-accent-900': '46 32 0',

  // Brand-mark faces (see the header for the measurements).
  '--color-mark-front': '0 38 118',
  '--color-mark-top':   '0 28 87',
  '--color-mark-side':  '0 18 56',
  '--color-mark-front-dm': '41 110 255',
  '--color-mark-top-dm':   '0 74 230',
  '--color-mark-side-dm':  '0 54 168',
};
